import { useEffect, useMemo } from "react";
import { FilterType } from "@/components/tables/types";
import {
  useDatatableContext,
  FilterConfig,
} from "@/components/tables/contexts/DatatableContextProvider";
import type { QueryFilterOptions } from "../types";

/**
 * Registers the filter-only More-Filters entries for the Queries page —
 * the filters whose corresponding columns were removed from the table
 * (Workspace, User) but whose filtering capability is still needed.
 *
 * Mirrors the pattern in ``useRegisterOpportunityFilters``: declaring
 * filters via ``registerFilters`` puts them in the More-Filters drawer
 * and on the URL via the context's ``useSearchParamsSync`` without
 * having to add hidden columns. Drilldown URLs like
 * ``/queries?userName=alice`` or ``/queries?workspaceId=ws-1`` from
 * sibling pages (``DatabricksUserList``, the workspace selector) keep
 * working because the single-value URL parses as a one-element array.
 *
 * The Compute and Query Type column-header filters live where they
 * already do (on the column definitions in ``queryTableColumns.tsx``);
 * this hook only covers the column-less ones.
 */
export function useRegisterDatabricksQueryFilters(
  filterOptions: QueryFilterOptions | undefined
): void {
  const { registerFilters } = useDatatableContext();

  const filterConfigs = useMemo(() => {
    const configs: Record<string, FilterConfig> = {
      userName: {
        label: "User",
        searchKey: "userName",
        filterType: FilterType.DROPDOWN,
        isArray: true,
        options: (filterOptions?.user_names ?? []).map(name => ({
          label: name,
          value: name,
        })),
      },
      workspaceId: {
        label: "Workspace",
        searchKey: "workspaceId",
        filterType: FilterType.DROPDOWN,
        isArray: true,
        options: (filterOptions?.workspaces ?? []).map(w => ({
          label: w.name,
          value: w.id,
        })),
      },
    };
    return configs;
  }, [filterOptions]);

  useEffect(() => {
    registerFilters(filterConfigs);
  }, [filterConfigs, registerFilters]);
}
